import { useCallback, useMemo, useState } from "react";
import { GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import { LOCALSTORAGENAME } from "../data/constants";
import { getUserId } from "../services/stores/userStore";

/**
 * Bug 78034: DataGridPro keeps column visibility in its own internal state, which dies with the
 * component - so every "Manage columns" selection was lost as soon as the user navigated away.
 * This hook is the controlled replacement: it seeds the model from localStorage and writes every
 * change back, keyed per grid and per user (a shared machine must not inherit another user's
 * layout).
 *
 * All grids share one localStorage entry (LOCALSTORAGENAME.GRIDCOLUMNVISIBILITY) holding a
 * `{ "<userId>:<gridKey>": model }` map, so clearing the preference is a single key removal.
 */

type StoredColumnVisibility = Record<string, GridColumnVisibilityModel>;

const ANONYMOUS_USER = "anonymous";

const readStore = (): StoredColumnVisibility => {
  try {
    const raw = localStorage.getItem(LOCALSTORAGENAME.GRIDCOLUMNVISIBILITY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Anything but a plain object (an older/corrupt value) is treated as "no preference saved".
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as StoredColumnVisibility)
      : {};
  } catch {
    return {};
  }
};

export const usePersistedColumnVisibility = (
  gridKey: string,
  defaultModel: GridColumnVisibilityModel = {}
) => {
  // The userId is read once per mount: a login or impersonation switch remounts the grid.
  const storageKey = useMemo(() => `${getUserId() ?? ANONYMOUS_USER}:${gridKey}`, [gridKey]);

  const [visibility, setVisibility] = useState(() => ({
    key: storageKey,
    model: readStore()[storageKey] ?? defaultModel,
  }));

  // Some grids swap their whole column set without remounting (Error Log's Front-end/API toggle,
  // Post Interview Forms' my/hr view), which changes gridKey mid-life. Load that key's own saved
  // selection instead of carrying the previous set's over.
  if (visibility.key !== storageKey) {
    setVisibility({ key: storageKey, model: readStore()[storageKey] ?? defaultModel });
  }

  const setColumnVisibilityModel = useCallback(
    (model: GridColumnVisibilityModel) => {
      setVisibility({ key: storageKey, model });
      try {
        const store = readStore();
        store[storageKey] = model;
        localStorage.setItem(LOCALSTORAGENAME.GRIDCOLUMNVISIBILITY, JSON.stringify(store));
      } catch {
        // Storage disabled or full - the grid still honours the selection for this session.
      }
    },
    [storageKey]
  );

  return { columnVisibilityModel: visibility.model, setColumnVisibilityModel };
};
